import type { WizardFlowConfig } from '../../engine/types';
import { STEP_KEYS } from '../../registry/stepKeys';
import { basicInfoStepSchema } from '../shared/schemas';
import { padelTeamConfigStepSchema, padelFixtureConfigStepSchema } from './schemas';
import { z } from 'zod';

/**
 * Padel Tournament Flow Configuration
 * 
 * Defines the steps and their order for creating a Padel tournament.
 * Uses padel-specific steps for team config, fixture config, and schedule.
 * Uses shared steps for basic info, pricing, club selection, and summary.
 */
export const PADEL_FLOW: WizardFlowConfig = {
  id: 'padel-default',
  sportId: 'PADEL',
  name: 'Torneo de Pádel',
  steps: [
    {
      id: 'basic-info',
      title: 'Información',
      description: 'Nombre, descripción y banner del torneo',
      componentKey: STEP_KEYS.SHARED_BASIC_INFO,
      draftSliceKey: 'basicInfo',
      validationSchema: basicInfoStepSchema,
    },
    {
      id: 'fixture-config',
      title: 'Formato',
      description: 'Formato del torneo y categorías',
      componentKey: STEP_KEYS.PADEL_FIXTURE_CONFIG,
      draftSliceKey: 'fixtureConfig',
      validationSchema: padelFixtureConfigStepSchema,
    },
    {
      id: 'team-config',
      title: 'Equipos',
      description: 'Configuración de género y cantidad de equipos',
      componentKey: STEP_KEYS.PADEL_TEAM_CONFIG,
      draftSliceKey: 'teamConfig',
      validationSchema: padelTeamConfigStepSchema,
    },
    {
      id: 'pricing',
      title: 'Precio',
      description: 'Precio de inscripción y método de pago',
      componentKey: STEP_KEYS.SHARED_PRICING,
      draftSliceKey: 'pricing',
      validationSchema: z.object({
        price: z.object({
          value: z.number().positive('El precio debe ser mayor a 0'),
        }),
        paymentType: z.string(),
        isValid: z.literal(true, {
          errorMap: () => ({ message: 'El precio debe ser mayor a la comisión de GetMatch' }),
        }),
      }),
    },
    {
      id: 'tournament-config',
      title: 'Configuración',
      description: 'Fecha de cierre, duración de partidos y disponibilidad mínima',
      componentKey: STEP_KEYS.SHARED_TOURNAMENT_CONFIG,
      draftSliceKey: 'tournamentConfig',
      validationSchema: z
        .object({
          signupDeadlineDate: z.date({ required_error: 'La fecha de cierre es requerida' }),
          matchDuration: z.number().min(30, 'Mínimo 30 minutos'),
          minSchedulesRequired: z
            .number()
            .int('Debe ser un número entero')
            .min(1, 'Mínimo 1 horario')
            .nullable(),
          countsForCategoryRanking: z.boolean(),
          categoryRankingTarget: z.enum(['PLAYER_CATEGORY', 'FIXED_CATEGORY']),
          categoryRankingCategory: z
            .object({ id: z.number(), name: z.string(), minimumElo: z.number() })
            .passthrough()
            .nullable(),
          isValid: z.literal(true),
        })
        // The backend rejects FIXED_CATEGORY without a target category, and the error
        // would only surface on submit, at the end of the wizard.
        .refine(
          (config) =>
            !config.countsForCategoryRanking ||
            config.categoryRankingTarget !== 'FIXED_CATEGORY' ||
            config.categoryRankingCategory !== null,
          {
            message: 'Elegí la categoría destino del ranking',
            path: ['categoryRankingCategory'],
          },
        ),
    },
    {
      id: 'club-schedules',
      title: 'Clubs y Horarios',
      description: 'Selecciona los clubs y bloques de horario disponibles',
      componentKey: STEP_KEYS.SHARED_CLUB_SCHEDULES,
      draftSliceKey: 'clubSchedules',
      validationSchema: z.object({
        clubSchedules: z.array(z.object({
          clubId: z.string(),
          club: z.object({ id: z.string(), name: z.string() }),
          scheduleIds: z.array(z.string()),
        })).min(1, 'Debes seleccionar al menos un club'),
        // Weekly pattern for LONG tournaments (per-date scheduleIds don't apply there).
        weeklyScheduleTemplate: z.array(z.object({
          dayOfWeek: z.string(),
          startTime: z.string(),
          endTime: z.string(),
          courtsQuantity: z.number(),
        })).optional(),
        weekStartDate: z.string().optional(),
        weeklyGridId: z.string().optional(),
      }).refine(
        // Valid when there's per-date schedule selection OR (LONG) a start day + a weekly grid.
        (data) =>
          data.clubSchedules.some((c) => c.scheduleIds.length > 0) ||
          (Boolean(data.weekStartDate) && Boolean(data.weeklyGridId)),
        'Debes elegir el día de inicio y la grilla semanal',
      ),
    },
    {
      id: 'summary',
      title: 'Resumen',
      description: 'Revisa y confirma los datos del torneo',
      componentKey: STEP_KEYS.SHARED_SUMMARY,
      // No draftSliceKey - Summary is a read-only step
    },
  ],
};
